import { RefObject, useRef, useState } from 'react';
import { isNotEmpty, isTruthy } from 'utility/functions';
import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';
import { useRouter } from 'next/router';
import { shallowEqual } from 'fast-equals';
import useWindowSize from './useWindowSize';
import { nanoid } from '@reduxjs/toolkit';
import { isServerSide } from 'utility/constant';

export interface VirtualDomProps {
  uids: Array<string>;
  counter?: number;
}

interface VirtualDomHookType<T> {
  uid: string;
  keys: Array<string>;
  heights: Record<string, string>;
  wrapperRef: RefObject<T>;
}

const getTop = ({ uids, counter }: VirtualDomProps) =>
  (uids ?? [])
    .filter((id) => isNotEmpty(id))
    .filter((_, idx) => idx < (counter ?? 2))
    .map((id) => id!);

export function useVirtualDom<T>({ uids, counter }: VirtualDomProps): VirtualDomHookType<T> {
  const { isReady } = useRouter();
  const [uid, setUid] = useState('');
  const { top: wTop, height: wHeight, width: wWidth, left: wLeft } = useWindowSize();

  const wrapperRef = useRef<T>(null);
  const heightRef = useRef<Record<string, string>>({});

  const keys = useRef<Array<string>>(getTop({ uids, counter }));

  useIsomorphicLayoutEffect(() => {
    if (isServerSide) return;

    let nextKeys: Array<string> = [];

    if (wrapperRef.current instanceof HTMLElement) {
      let half = 0.5 * wHeight;
      if (half < 300) {
        half = 300;
      }
      const thresholdTop = half * -1;
      const thresholdBottom = wHeight + half;
      nextKeys = Array.from(wrapperRef.current?.querySelectorAll<HTMLElement>('[data-uid]')).reduce((acc, x) => {
        const { top, height } = x.getBoundingClientRect();
        const uid = x.dataset.uid;
        if (top <= thresholdBottom && top + height >= thresholdTop) {
          acc.push(uid!);
          heightRef.current[uid!] = `${height}px`;
        }
        return acc;
      }, [] as string[]);
    }

    if (isTruthy(nextKeys?.length) !== isTruthy(uids?.length)) {
      nextKeys = getTop({ uids, counter });
    }

    if (shallowEqual(keys.current, nextKeys)) return;

    keys.current = nextKeys;
    setUid(nanoid());
  }, [uids, counter, uid, wTop, wHeight, wWidth, wLeft, isReady]);

  return {
    uid,
    keys: keys.current,
    heights: heightRef.current,
    wrapperRef,
  };
}
